import React, { memo, useCallback, useEffect, useState } from "react";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";
import { Document } from "../../DocumentMasterTab";
import dateHandler from "@/composables/dateUtil";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
  getDocumentApprovalStatusStyle,
} from "@/constants/enums/document-approval-status.enum";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import MsgBlue from "@styles/all-images/icons/ChatIconBlue.svg";
import TimeBlue from "@styles/all-images/icons/timeblue.svg";
import { useNavigate, useParams } from "react-router-dom";
import infocircle from "@styles/all-images/icons/info.svg";
import CreateFolder from "@/pages/Documents/components/CreateFolder";
import ReuploadIcon from "@styles/all-images/icons/uploadblue.svg";
import FileTimeLineRejectedReason from "@/pages/T4/T4FilesTimeline/FileTimeLineRejectedReason";
import { useOnboardingDocumentModuleContext } from "../../Provider/OnboardingDocumentModuleProvider";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "@/pages/LiveChat/StandaloneChatDrawer";
import { DocumentInfo } from "@/pages/LiveChat/StandaloneChatDrawer/ChatComponent";
import { toast } from "react-toastify";
import FileUploadModal from "@/components/common/FileUploadModal";
import { AcceptedFileTypes } from "@/components/common/inputs/FileUpload";
import ViewRejectionReason from "@/components/common/ViewRejectedReason";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
const usertype = JSON.parse(localStorage.getItem("user") || "{}")?.user_type
  ?.id;
interface DocumentsTableProps {
  documents: Document[];
  setForceRefreshMaster: (value: boolean) => void;
}

interface Pagination {
  count: number;
  page: number;
  limit: number;
}

interface FetchResponse {
  ok: boolean;
  data: {
    data: Document[];
    meta: Pagination;
  };
}

const DocumentsTable = memo<DocumentsTableProps>(
  ({ documents, setForceRefreshMaster }) => {
    const { id: userParamId } = useParams();
    const { formatDate } = dateHandler();
    const [forceRefresh, setForceRefresh] = useState(false);
    const navigate = useNavigate();
    const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
    const [selectedRejectionNote, setSelectedRejectionNote] = useState<
      string | null
    >(null);
    const [isReuploadModalOpen, setIsReuploadModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(
      null,
    );
    const { isApprovedView } = useOnboardingDocumentModuleContext();
    const [settingRefresh, setSettingRefresh] = useState(false);
    const [documentChatUserId, setDocumentChatUserId] = useState<number | null>(
      null,
    );
    const [documentChatDocumentInfo, setDocumentChatDocumentInfo] =
      useState<DocumentInfo | null>(null);
    const [documentChatRoomId, setDocumentChatRoomId] = useState<string | null>(
      null,
    );
    const [isDocumentChatOpen, setIsDocumentChatOpen] = useState(false);
    const user = useSelector((state: RootState) => state.auth.user);

    const isChatDisabled = (row: Document) => {
      return (
        !row.uploaded_document.room_id &&
        user?.user_type?.id === UserTierType.Tier4
      );
    };

    const columns: TableColumn<Document>[] = [
      {
        header: "Document Name",
        accessor: "document_name",
        sortable: true,
        sortKey: "document_name",
      },
      {
        header: "Document Provided",
        accessor: (row: Document) => {
          // return row.uploaded_document?.mark_as_na
          //   ? "N/A"
          //   : "Yes";
          const { bgColor, textColor } = getDocumentApprovalStatusStyle(
            row.uploaded_document.status as DocumentApprovalStatus,
          );

          return (
            <div className="tw-flex tw-items-center tw-gap-2">
              <li
                className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${row.uploaded_document?.mark_as_na ? "tw-bg-[#FDD4D2] tw-text-[#B54708]" : "tw-bg-[#ECFDF3] tw-text-[#027A48]"}`}
              >
                {row.uploaded_document?.mark_as_na ? "N/A" : "Yes"}
              </li>
            </div>
          );
        },
        sortable: true,
        sortKey: "uploaded_document.mark_as_na",
      },
      {
        header: "Uploaded On",
        accessor: (row: Document) => {
          return row.uploaded_document?.created_at
            ? formatDate(row.uploaded_document.created_at)
            : "-";
        },
        sortable: true,
        sortKey: "uploaded_document.created_at",
      },
      {
        header: "Status",
        accessor: (row: Document) => {
          const { bgColor, textColor } = getDocumentApprovalStatusStyle(
            row.uploaded_document.status as DocumentApprovalStatus,
          );

          return (
            <div className="tw-flex tw-items-center tw-gap-2">
              <li
                className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${bgColor} ${textColor}`}
              >
                {DocumentApprovalStatusText[
                  row.uploaded_document.status as DocumentApprovalStatus
                ] || NO_DATA_AVAILABLE}
              </li>
              {row.uploaded_document.status ===
                DocumentApprovalStatus.rejected && (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <button
                    onClick={() =>
                      handleOpenRejectionModal(
                        row.uploaded_document.note || null,
                      )
                    }
                  >
                    <img
                      src={infocircle}
                      alt="info"
                      className="tw-w-4 tw-h-4"
                    />
                  </button>
                </div>
              )}
            </div>
          );
        },
        sortKey: "status",
      },
      {
        header: "Actions",
        accessor: (row: Document) => (
          <div className="tw-flex tw-items-center">
            {row.uploaded_document && (
              <>
                <button
                  onClick={() => handleView(row)}
                  className="tw-p-2 tw-rounded-full hover:tw-bg-gray-100"
                  title="View"
                >
                  <img
                    src={EyeIcon}
                    alt="View"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>

                <button
                  onClick={() => handleChat(row)}
                  className={`tw-p-2 tw-rounded-full ${
                    isChatDisabled(row)
                      ? "tw-opacity-50 tw-cursor-not-allowed"
                      : "hover:tw-bg-gray-100"
                  }`}
                  disabled={isChatDisabled(row)}
                  title="Chat"
                >
                  <img
                    src={MsgBlue}
                    alt="Chat"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>

                <button
                  onClick={() => handleHistory(row)}
                  className={`tw-p-2 tw-rounded-full ${
                    row.uploaded_document.status ===
                    DocumentApprovalStatus.pending
                      ? "tw-opacity-50 tw-cursor-not-allowed"
                      : "hover:tw-bg-gray-100"
                  }`}
                  disabled={
                    row.uploaded_document.status ===
                    DocumentApprovalStatus.pending
                  }
                  title="History"
                >
                  <img
                    src={TimeBlue}
                    alt="History"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>

                {usertype !== 3 && (
                  <button
                    onClick={() => handleReupload(row)}
                    className={`tw-p-2 tw-rounded-full ${
                      row.uploaded_document.status ===
                        DocumentApprovalStatus.pending ||
                      row.uploaded_document.status ===
                        DocumentApprovalStatus.approved
                        ? "tw-opacity-50 tw-cursor-not-allowed"
                        : "hover:tw-bg-gray-100"
                    }`}
                    disabled={
                      row.uploaded_document.status ===
                        DocumentApprovalStatus.pending ||
                      row.uploaded_document.status ===
                        DocumentApprovalStatus.approved
                    }
                    title="Reupload"
                  >
                    <img
                      src={ReuploadIcon}
                      alt="reupload"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                )}
              </>
            )}
          </div>
        ),
      },
    ];

    const handleFetch = useCallback(
      async (params: any) => {
        const { page, limit, search, sortColumn, sortDirection } = params;

        let filteredData = [...documents];

        if (search) {
          filteredData = filteredData.filter((doc) =>
            doc.document_name.toLowerCase().includes(search.toLowerCase()),
          );
        }

        if (sortColumn) {
          filteredData.sort((a, b) => {
            let aValue = a[sortColumn as keyof Document];
            let bValue = b[sortColumn as keyof Document];

            if (sortColumn === "uploaded_document.created_at") {
              aValue = a.uploaded_document?.created_at || "";
              bValue = b.uploaded_document?.created_at || "";
            }

            if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
            return 0;
          });
        }

        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        return new Promise<FetchResponse>((resolve) => {
          setTimeout(() => {
            resolve({
              ok: true,
              data: {
                data: paginatedData,
                meta: {
                  count: filteredData.length,
                  page,
                  limit,
                },
              },
            });
          }, 500);
        });
      },
      [documents],
    );

    const handleView = useCallback((document: Document) => {
      const currentPathName = window.location.pathname;
      const path = isApprovedView
        ? "/t4/view-onboarding-document/view-approved"
        : "/t4/view-onboarding-document/view";
      navigate(
        `${path}/${document.uploaded_document.id}?from=${currentPathName}`,
      );
    }, []);

    const handleChat = (document: Document) => {
      if (document.uploaded_document.id) {
        const userId =
          user?.user_type?.id === UserTierType.Tier4 ? user?.id : userParamId;
        setDocumentChatUserId(userId);
        setDocumentChatDocumentInfo({
          id: document.uploaded_document.id,
          title: document.document_name,
          status: document.uploaded_document.status,
        });
        setDocumentChatRoomId(document.uploaded_document.room_id?.toString());
        setIsDocumentChatOpen(true);
      } else {
        toast.warning("Chat is not available for this document");
      }
    };

    const handleHistory = useCallback((document: Document) => {
      const currentPathName = window.location.pathname;
      navigate(
        `/t4/view-onboarding-document/history/${document.uploaded_document.id}?from=${currentPathName}`,
      );
    }, []);

    const handleOpenRejectionModal = (note: string | null) => {
      setSelectedRejectionNote(note);
      setIsRejectionModalOpen(true);
    };

    const handleCloseRejectionReasonModal = () => {
      setIsRejectionModalOpen(false);
    };

    const handleReupload = (row: any) => {
      setSelectedDocument(row);
      setIsReuploadModalOpen(true);
    };

    useEffect(() => {
      setForceRefresh(false);
      setSettingRefresh(true);
    }, [documents]);

    useEffect(() => {
      if (settingRefresh) {
        setForceRefresh(true);
        setSettingRefresh(false);
      }
    }, [settingRefresh]);

    return (
      <>
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          hideControls={true}
          removeInitialPadding={true}
          hidePagination={true}
          noDataMessage="No documents found"
          noSearchDataMessage="No matching documents found"
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
        />
        {isRejectionModalOpen && (
          // <FileTimeLineRejectedReason
          //   onClose={() => setIsRejectionModalOpen(false)}
          //   onSubmit={() => setIsRejectionModalOpen(false)}
          //   rejectionData={{
          //     title: "Document Rejection Reason",
          //     comment: selectedRejectionNote || "",
          //   }}
          // />
          <ViewRejectionReason
            onClose={() => setIsRejectionModalOpen(false)}
            rejectionReason={selectedRejectionNote || ""}
          />
        )}
        {isReuploadModalOpen && selectedDocument && (
          <FileUploadModal
            open={isReuploadModalOpen}
            documentTypeId={selectedDocument.id}
            title="File Upload"
            onClose={() => {
              setIsReuploadModalOpen(false);
              setSelectedDocument(null);
            }}
            acceptedFileTypes={[
              AcceptedFileTypes.PDF,
              AcceptedFileTypes.DOC,
              AcceptedFileTypes.DOCX,
              AcceptedFileTypes.XLS,
              AcceptedFileTypes.XLSX,
              AcceptedFileTypes.CSV,
              AcceptedFileTypes.PNG,
              AcceptedFileTypes.JPG,
              AcceptedFileTypes.JPEG,
              AcceptedFileTypes.SVG,
            ]}
            maxFileSize={25 * 1000 * 1000}
            folderId={selectedDocument.uploaded_document.folder_id}
            onSuccess={() => {
              console.log("onSuccess: ");
              setIsReuploadModalOpen(false);
              setSelectedDocument(null);
              setForceRefreshMaster(true);
            }}
            isDriveUpload={false}
          />
        )}
        {documentChatUserId && (
          <StandaloneChatDrawer
            isChatNotificationOpen={isDocumentChatOpen}
            setIsChatNotificationOpen={setIsDocumentChatOpen}
            documentInfo={documentChatDocumentInfo || undefined}
            documentId={documentChatDocumentInfo?.id || undefined}
            userId={documentChatUserId}
            customChatRoomId={documentChatRoomId}
            type={StandaloneChatDrawerType.DOCUMENT}
          />
        )}
      </>
    );
  },
);

DocumentsTable.displayName = "DocumentsTable";

export default DocumentsTable;
